import { useState, useEffect, useRef, FormEvent } from 'react';

const SUBMIT_GHL_URL = `https://kkmawmfkuffejnemowho.supabase.co/functions/v1/submit-to-ghl`;
const GHL_BOOKING_URL = 'https://api.leadconnectorhq.com/widget/booking/BbTNQ40bgtpSYcpM0mTG';

type View = 'closed' | 'panel' | 'booking' | 'launching';

function triggerAgent() {
  const btn = document.querySelector<HTMLElement>('#vapi-widget-floating-button');
  if (btn) btn.click();
}

export default function LiveChat() {
  const [view, setView] = useState<View>('closed');
  const [pulse, setPulse] = useState(true);
  const [unread] = useState(1);
  const [iframeHeight, setIframeHeight] = useState(680);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (view !== 'closed') setPulse(false);
    if (view === 'panel') setTimeout(() => nameRef.current?.focus(), 250);
  }, [view]);

  // Listen for GHL iframe height messages
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!e.data) return;
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (data?.type === 'iframeHeight' && typeof data.value === 'number') {
          setIframeHeight(Math.max(600, data.value + 40));
        }
        if (typeof data === 'number' && data > 200) {
          setIframeHeight(Math.max(600, data + 40));
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const openBookingInPanel = () => setView('booking');

  const handleLaunch = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setSubmitting(true);
    setView('launching');

    fetch(SUBMIT_GHL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        email,
        source: 'Live Chat',
        tags: ['live-chat'],
        noteBody: [
          '💬 AI Chat Started',
          '──────────────────────',
          `Name: ${name}`,
          `Email: ${email}`,
          `Started: ${new Date().toLocaleString('en-GB')}`,
          'Channel: AI Solar Assistant (Readdy Agent)',
        ].join('\n'),
      }),
    }).catch(() => {});

    setTimeout(() => {
      setView('closed');
      setSubmitting(false);
      triggerAgent();
    }, 900);
  };

  const handleSkip = () => {
    setView('closed');
    triggerAgent();
  };

  const handleClose = () => setView('closed');
  const handleBackToPanel = () => setView('panel');

  return (
    <>
      {/* ── Panel ──────────────────────────────────────────── */}
      <div
        className={`fixed bottom-24 md:bottom-28 right-4 md:right-6 z-[55] w-[calc(100vw-2rem)] max-w-[360px] bg-white rounded-2xl overflow-hidden transition-all duration-300 origin-bottom-right border border-gray-100 flex flex-col ${
          view !== 'closed' ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-90 pointer-events-none'
        }`}
        style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.12)', maxHeight: '82vh' }}
      >
        {/* ── HEADER — changes based on view ── */}
        <div className="bg-[#145A32] px-4 py-3.5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {view === 'booking' && (
              <button
                onClick={handleBackToPanel}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer flex-shrink-0 mr-1"
              >
                <i className="ri-arrow-left-line text-white text-base" />
              </button>
            )}
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#7AC943]/20 flex-shrink-0">
              <i className={`${view === 'booking' ? 'ri-calendar-check-line' : 'ri-sparkling-line'} text-[#7AC943] text-lg`} />
            </div>
            <div>
              <div className="text-white font-bold text-sm leading-tight">
                {view === 'booking' ? 'Book an Appointment' : 'Solar Assistant'}
              </div>
              <div className="text-white/55 text-xs">
                {view === 'booking' ? 'Mon–Sat · London, Essex, Suffolk & Cambridge' : 'AI-powered · Knows everything about solar'}
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-white text-lg" />
          </button>
        </div>

        {/* ── BOOKING VIEW — GHL calendar embedded ── */}
        {view === 'booking' && (
          <div className="flex-1 overflow-y-auto overflow-x-hidden bg-white">
            <iframe
              src={GHL_BOOKING_URL}
              title="Book an Appointment"
              style={{
                width: '100%',
                height: `${iframeHeight}px`,
                border: 'none',
                display: 'block',
              }}
              scrolling="yes"
            />
          </div>
        )}

        {/* ── LAUNCHING ── */}
        {view === 'launching' && (
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#7AC943]/15 relative">
              <i className="ri-sparkling-fill text-[#7AC943] text-2xl" />
              <span className="absolute inset-0 rounded-full border-4 border-[#7AC943]/20 animate-ping" />
            </div>
            <div>
              <p className="font-bold text-[#1F2933] text-sm">Launching your Solar Assistant…</p>
              <p className="text-gray-400 text-xs mt-1">Your details have been saved — we&apos;ll follow up too!</p>
            </div>
          </div>
        )}

        {/* ── MAIN PANEL ── */}
        {view === 'panel' && (
          <div className="overflow-y-auto flex-1">

            {/* Agent intro bubble */}
            <div className="p-4 pb-2">
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#145A32] flex-shrink-0 mt-0.5">
                  <i className="ri-sparkling-line text-[#7AC943] text-sm" />
                </div>
                <div className="bg-[#f3f4f6] rounded-2xl rounded-tl-none px-4 py-3 text-sm text-[#1F2933] leading-relaxed max-w-[230px]">
                  👋 Hi! I&apos;m your AI solar assistant. Ask me anything about solar, battery storage, EV chargers, costs or booking — I know it all!
                </div>
              </div>
            </div>

            {/* Topic chips */}
            <div className="px-4 pb-3 flex flex-wrap gap-1.5">
              {['How much does solar cost?', 'What size system?', 'Battery storage', 'EV charger install', 'Book a survey'].map((chip) => (
                <button
                  key={chip}
                  onClick={handleSkip}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#145A32]/20 text-[#145A32] bg-[#145A32]/5 hover:bg-[#145A32]/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {chip}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 px-4 pb-3">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold whitespace-nowrap">Quick options</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* Quick actions */}
            <div className="px-4 pb-4 flex flex-col gap-2">
              <a
                href="/solar-quote"
                className="flex items-center gap-3 bg-[#f9fafb] hover:bg-[#f3f4f6] border border-gray-100 rounded-xl px-3 py-2.5 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7AC943]/15 flex-shrink-0">
                  <i className="ri-sun-line text-[#7AC943] text-base" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#1F2933]">Get a solar quote</div>
                  <div className="text-[10px] text-gray-400">Free, no-obligation — 2 min quiz</div>
                </div>
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-s-line text-gray-400" />
                </div>
              </a>

              {/* Book appointment — opens GHL calendar IN the panel */}
              <button
                onClick={openBookingInPanel}
                className="flex items-center gap-3 bg-[#145A32] hover:bg-[#0e4226] rounded-xl px-3 py-2.5 transition-colors cursor-pointer text-left w-full"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/15 flex-shrink-0">
                  <i className="ri-calendar-check-line text-white text-base" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white">Book an appointment</div>
                  <div className="text-[10px] text-white/60">Pick a date &amp; time — powered by GHL</div>
                </div>
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-s-line text-white/70" />
                </div>
              </button>

              <a
                href="tel:03300101234"
                className="flex items-center gap-3 bg-[#f9fafb] hover:bg-[#f3f4f6] border border-gray-100 rounded-xl px-3 py-2.5 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#4CAF50]/10 flex-shrink-0">
                  <i className="ri-phone-line text-[#4CAF50] text-base" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#1F2933]">Call 0330 010 1234</div>
                  <div className="text-[10px] text-gray-400">Mon–Sat, 8am–6pm</div>
                </div>
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-s-line text-gray-400" />
                </div>
              </a>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 px-4 pb-3">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold whitespace-nowrap">chat with AI assistant</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* GHL capture + launch form */}
            <form onSubmit={handleLaunch} className="px-4 pb-5 flex flex-col gap-3">
              <p className="text-xs text-gray-500 leading-relaxed">
                Enter your details and our AI assistant opens instantly — your info is also saved so our team can follow up if needed.
              </p>
              <input
                ref={nameRef}
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors text-[#1F2933]"
              />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors text-[#1F2933]"
              />
              {error && (
                <p className="text-red-500 text-xs bg-red-50 rounded-lg px-3 py-2 border border-red-100">{error}</p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#145A32] hover:bg-[#0e4226] text-white font-bold py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-sparkling-line" />
                </div>
                Chat with Solar Assistant →
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer underline underline-offset-2 text-center"
              >
                Skip — just open the assistant
              </button>
            </form>
          </div>
        )}
      </div>

      {/* ── Floating Bubble ─────────────────────────────── */}
      <button
        onClick={() => setView(view === 'closed' ? 'panel' : 'closed')}
        className="fixed bottom-20 md:bottom-24 right-4 md:right-6 z-[55] w-14 h-14 flex items-center justify-center rounded-full bg-[#145A32] hover:bg-[#0e4226] transition-all duration-200 cursor-pointer"
        style={{ boxShadow: '0 4px 20px rgba(20,90,50,0.35)' }}
        aria-label="Open Solar Assistant"
      >
        {pulse && view === 'closed' && (
          <span className="absolute w-14 h-14 rounded-full bg-[#4CAF50]/40 animate-ping" />
        )}
        <div className="w-6 h-6 flex items-center justify-center">
          <i className={`${view !== 'closed' ? 'ri-close-line' : 'ri-sparkling-line'} text-white text-xl`} />
        </div>
        {view === 'closed' && unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-[#7AC943] text-white text-[9px] font-black rounded-full border-2 border-white">
            AI
          </span>
        )}
      </button>
    </>
  );
}